import { useState, useEffect, useCallback, useRef } from 'react';

interface TurnstileRenderOptions {
  sitekey: string;
  size?: string;
  callback?: (token: string) => void;
  'expired-callback'?: () => void;
  'error-callback'?: () => void;
}

interface TurnstileInstance {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  execute: (widgetId: string) => void;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileInstance;
  }
}

interface UseTurnstileOptions {
  // Disparados quando o desafio falha ou expira. O Turnstile é invisível: sem
  // estes callbacks, a falha (comum em WebView) é silenciosa e o consumidor fica
  // esperando um token que nunca chega.
  onError?: () => void;
  onExpired?: () => void;
}

interface UseTurnstileReturn {
  token: string | null;
  execute: () => void;
  reset: () => void;
  isReady: boolean;
}

const SITE_KEY: string | undefined = import.meta.env.VITE_TURNSTILE_SITE_KEY;

export function useTurnstile(
  containerRef: React.RefObject<HTMLElement | null>,
  opts?: UseTurnstileOptions
): UseTurnstileReturn {
  const [token, setToken] = useState<string | null>(null);
  const [isReady, setIsReady] = useState<boolean>(false);
  const widgetIdRef = useRef<string | null>(null);
  // Guarda os callbacks num ref para não re-renderizar o widget quando o
  // consumidor passar funções novas a cada render (o effect depende só do ref).
  const optsRef = useRef<UseTurnstileOptions | undefined>(opts);
  optsRef.current = opts;

  useEffect(() => {
    if (!SITE_KEY || !containerRef?.current) return;

    // Esperar o script do Turnstile carregar (é async)
    const interval = setInterval(() => {
      if (window.turnstile) {
        clearInterval(interval);
        widgetIdRef.current = window.turnstile.render(containerRef.current!, {
          sitekey: SITE_KEY,
          size: 'invisible',
          callback: (newToken: string) => {
            setToken(newToken);
          },
          'expired-callback': () => {
            setToken(null);
            optsRef.current?.onExpired?.();
          },
          'error-callback': () => {
            setToken(null);
            optsRef.current?.onError?.();
          },
        });
        setIsReady(true);
      }
    }, 100);

    return () => {
      clearInterval(interval);
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, [containerRef]);

  const execute = useCallback((): void => {
    if (widgetIdRef.current !== null && window.turnstile) {
      window.turnstile.execute(widgetIdRef.current);
    }
  }, []);

  const reset = useCallback((): void => {
    setToken(null);
    if (widgetIdRef.current !== null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  }, []);

  return { token, execute, reset, isReady };
}
